import { useState, useContext } from 'react'
import { useAppDispatch } from '@/store/store-hooks'
import { AuthContext } from '@/context/auth-context'
import OrganizationSetup from '@/organizations/views/create-organization/OrganizationSetup'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { GlobalToastType, kToastDurationShortMs, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { postCreateOrganization } from '@/organizations/fetch/post-create-organization'
import { InviteEmailStatus, type CreateSelfServeOrganizationRequest } from '@/organizations/schemas'
import { BlockedWorkEmailApiError } from '@/organizations/work-email-policy-error'
import { useNavigate } from 'react-router-dom'
import { getOrganizationInviteEmailError } from '@/organizations/utils/organization-invite-email'
import { OrganizationRosterEntrySource } from '@/analytics/enums/organization.enums'
import { organizationRosterLocationState } from '@/organizations/utils/organization-roster-navigation'

// The backend sets the organization_id Firebase custom claim during creation, but the client ID
// token only reflects it once it refreshes AND the claim has propagated (which is not immediate).
// Refresh the account data up to this many times, waiting between attempts, until the claim lands so
// org-gated UI (the roster) can render right after creation.
const kOrgClaimRefreshMaxAttempts = 6
const kOrgClaimRefreshDelayMs = 1000

type CreateOrganizationCardProps = {
  /** Flat layout for the settings modal (no card chrome); the panel supplies the surrounding padding. */
  embedded?: boolean
}

const CreateOrganizationCard = ({ embedded = false }: CreateOrganizationCardProps) => {
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()
  const navigate = useNavigate()

  const { refreshUserAccountData, user, userAccountData } = useContext(AuthContext)
  const isSubscriptionExempt = userAccountData?.isSubscriptionExempt

  const [orgName, setOrgName] = useState('')
  const [emails, setEmails] = useState<string[]>([])
  const [emailValue, setEmailValue] = useState('')
  const [isOrgCreationInProgress, setIsOrgCreationInProgress] = useState(false)

  const handleCreateOrganization = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (orgName.trim()) {
      const pendingEmail = emailValue.trim()
      const pendingEmailError = pendingEmail ? getOrganizationInviteEmailError(pendingEmail, emails, user?.email) : null
      if (pendingEmailError) {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: pendingEmailError, durationMs: kToastDurationMediumMs }))
        return
      }

      try {
        setIsOrgCreationInProgress(true)

        const submittedEmails = pendingEmail ? [...emails, pendingEmail] : emails
        const invitee_emails = submittedEmails.map((email) => ({ invitee_email: email }))
        const createOrganizationRequest: CreateSelfServeOrganizationRequest = { organization: { name: orgName, invites: invitee_emails } }
        const result = await postCreateOrganization(createOrganizationRequest)

        // Refresh until the org claim propagates (bounded). If it never lands the org is still
        // created — we just proceed, and the roster below won't auto-open.
        let refreshedAccountData = await refreshUserAccountData()
        for (let attempt = 1; attempt < kOrgClaimRefreshMaxAttempts && !refreshedAccountData?.isOrgAccount; attempt++) {
          await new Promise((resolve) => setTimeout(resolve, kOrgClaimRefreshDelayMs))
          refreshedAccountData = await refreshUserAccountData()
        }
        trackEvent(AnalyticsEvent.OrganizationCreated, {
          organizationId: result.organization.id || '',
          organizationCreatedInvitesCount: invitee_emails.length,
        })

        const failedInviteCount = result.invite_email_results.filter(({ email_status }) => email_status === InviteEmailStatus.FAILED).length
        const redirectedInviteCount = result.invite_email_results.filter(({ email_status }) => email_status === InviteEmailStatus.REDIRECTED_TEST_INBOX).length
        const sentInviteCount = result.invite_email_results.filter(({ email_status }) => email_status === InviteEmailStatus.SENT).length
        if (failedInviteCount > 0) {
          dispatch(
            openGlobalToast({
              type: GlobalToastType.ERROR,
              message: `Organization created, but ${failedInviteCount} invitation${failedInviteCount === 1 ? '' : 's'} could not be sent.`,
              durationMs: kToastDurationMediumMs,
            })
          )
        } else if (redirectedInviteCount > 0) {
          dispatch(
            openGlobalToast({
              type: GlobalToastType.SUCCESS,
              message: `Organization created. ${redirectedInviteCount} invitation${redirectedInviteCount === 1 ? '' : 's'} redirected to the test inbox.`,
              durationMs: kToastDurationMediumMs,
            })
          )
        } else {
          // Only claim delivery when every submitted invite has an explicit SENT result. A missing
          // or incomplete result set (e.g. an older backend that omits invite_email_results, which
          // the schema defaults to []) is treated as unknown rather than a false "sent" confirmation.
          const allInvitesConfirmedSent = invitee_emails.length > 0 && sentInviteCount === invitee_emails.length
          const message = allInvitesConfirmedSent ? 'Organization created and invitations sent.' : 'Organization created.'
          dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message, durationMs: kToastDurationShortMs }))
        }

        navigate('/dashboard/matters', { replace: true, state: organizationRosterLocationState(OrganizationRosterEntrySource.OrgCreation) })
      } catch (error) {
        console.error('Failed to create organization', error)
        const errorMessage = error instanceof BlockedWorkEmailApiError ? error.message : 'Failed to create organization, please try again.'
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: errorMessage, durationMs: kToastDurationMediumMs }))
      } finally {
        setIsOrgCreationInProgress(false)
      }
    }
  }

  const isCreateDisabled = !orgName.trim() || isOrgCreationInProgress

  return (
    <div className={embedded ? '' : 'bg-brand-neutral-50 shadow rounded-lg mt-6 p-6'}>
      {/* Create Organization Header */}
      <h2 className="font-rethink-sans text-xl font-semibold leading-7 text-brand-neutral-950">Create an organization</h2>
      <p className="mt-2 text-sm text-brand-neutral-600">
        Unlock collaboration features{!isSubscriptionExempt && ', centralize billing for your team,'} and manage organizational settings.
      </p>

      {/* Organization Form */}
      <OrganizationSetup
        orgName={orgName}
        setOrgName={setOrgName}
        emails={emails}
        setEmails={setEmails}
        emailValue={emailValue}
        setEmailValue={setEmailValue}
      />

      {/* Create Organization Button */}
      <div className="mt-6 flex items-center justify-end space-x-4">
        <button
          type="button"
          className={`flex items-center gap-2 rounded-md bg-brand-500 px-3.5 py-2.5 text-sm font-semibold text-white enabled:hocus:bg-brand-800 focus:outline-hidden disabled:cursor-not-allowed disabled:bg-true-gray-300`}
          onClick={handleCreateOrganization}
          disabled={isCreateDisabled}
        >
          Create my organization
          {isOrgCreationInProgress && <CircularProgressContinuousSized hexColor={'#eee'} size={20} thickness={4} />}
        </button>
      </div>
    </div>
  )
}

export default CreateOrganizationCard
